import { keepPreviousData, type Query, type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import isObject from 'lodash/isObject';

import { Boards, type GetBoardStatsParams, type GetBoardStatsResponse } from '@air/api';

export const PRIVATE_BOARD_STATS = 'PRIVATE_BOARD_STATS';

export type PrivateBoardStatsKey = [typeof PRIVATE_BOARD_STATS, PrivateBoardStatsParams];

export const getPrivateBoardStatsKey = (params: PrivateBoardStatsParams) => {
  return [PRIVATE_BOARD_STATS, params];
};

export const isPrivateBoardStatsKey = (key: QueryKey): key is PrivateBoardStatsKey => {
  return Array.isArray(key) && key[0] === PRIVATE_BOARD_STATS && isObject(key[1]);
};

export type PrivateBoardStatsParams = Omit<GetBoardStatsParams, 'boardId' | 'workspaceId'> & {
  boardId: GetBoardStatsParams['boardId'] | undefined;
  workspaceId: GetBoardStatsParams['workspaceId'] | undefined;
};

export type UsePrivateBoardStatsOptions = {
  enabled?: boolean;
  keepPreviousData?: boolean;
  /**
   * Like `keepPreviousData`, but only while the board stays the same (e.g. toggling nested content), so a mounted
   * observer never shows the previous board's stats after navigating to another board.
   */
  keepPreviousDataWithinBoard?: boolean;
};

const getPlaceholderData = (boardId: PrivateBoardStatsParams['boardId'], options?: UsePrivateBoardStatsOptions) => {
  if (options?.keepPreviousData) {
    return keepPreviousData;
  }

  if (options?.keepPreviousDataWithinBoard) {
    return (previousData: GetBoardStatsResponse | undefined, previousQuery?: Query<GetBoardStatsResponse>) =>
      previousQuery && isPrivateBoardStatsKey(previousQuery.queryKey) && previousQuery.queryKey[1].boardId === boardId
        ? previousData
        : undefined;
  }

  return undefined;
};

/**
 * @deprecated The backend has marked this endpoint as deprecated. Moving forward, we should
 * utilize `includeDirectDescendantCount` and `includeDirectDescendantBoardCount` when fetching
 * the board and use `directDescendantCount` and `directDescendantBoardCount` from the board reponse.
 */
export const usePrivateBoardStats = (params: PrivateBoardStatsParams, options?: UsePrivateBoardStatsOptions) => {
  const boardId = params.boardId;
  const workspaceId = params.workspaceId;

  return useQuery({
    queryKey: getPrivateBoardStatsKey(params),

    queryFn:
      boardId && workspaceId
        ? () => {
            return Boards.getBoardStats({
              ...params,
              boardId,
              workspaceId,
            });
          }
        : skipToken,
    enabled: !!boardId && !!workspaceId && options?.enabled,
    placeholderData: getPlaceholderData(boardId, options),
  });
};
